<template>
  <div class="metrics-student-class-ranking">
    <div class="metrics-student-class-ranking__header">
      <span class="metrics-student-class-ranking__title heading-s">
        {{ $t("metrics.topStudentsByClass") }}
      </span>
    </div>

    <infinity-loading v-if="isLoading && !hasData" />

    <template v-else-if="hasData">
      <div
        v-if="rankSummary"
        class="metrics-student-class-ranking__summary paragraph-s"
      >
        {{ rankSummary }}
      </div>

      <div class="metrics-student-class-ranking__table-wrap">
        <eg-pv-table
          :items="tableItems"
          :fields="tableFields"
          :row-class="rowClass"
          @row-click="onRowClick"
        >
          <template #cell(student)="{ item }">
            <div class="metrics-student-class-ranking__student-cell">
              <div class="metrics-student-class-ranking__avatar">
                {{ initials(item) }}
              </div>
              <span class="metrics-student-class-ranking__name">
                {{ item.firstName }} {{ item.lastName }}
              </span>
              <eg-icon
                v-if="item.isCurrentStudent"
                icon="student-award"
                class="metrics-student-class-ranking__current-icon"
              />
            </div>
          </template>

          <template #cell(avgScore)="{ item }">
            {{ Math.round(item.avgScore) }}%
          </template>

          <template
            v-for="assignment in assignments"
            #[`cell(assignment_${assignment.id})`]="{ item }"
          >
            <span
              v-if="item.scores[assignment.id] != null"
              :key="assignment.id"
              class="metrics-student-class-ranking__score-badge"
              :class="scoreBadgeClass(item.scores[assignment.id])"
            >
              {{ item.scores[assignment.id] }}%
            </span>
            <span v-else :key="`empty_${assignment.id}`">—</span>
          </template>
        </eg-pv-table>
      </div>
    </template>

    <metrics-empty-state
      v-else-if="!isLoading"
      :description="emptyDescription"
    />
  </div>
</template>

<script>
import MetricsEmptyState from "../shared/metrics_empty_state.vue";
import InfinityLoading from "@/components/rubrics/newRubric/components/infinity-loading.vue";
import EgPvTable from "@/components/global/eg/eg_pv_table/eg_pv_table.vue";
import { getScoreTier, studentInitials } from "@/utils/metricsUtils";
import metricsViewMixin from "@/mixins/metricsViewMixin";

export default {
  name: "MetricsStudentClassRanking",

  components: {
    MetricsEmptyState,
    InfinityLoading,
    EgPvTable,
  },

  mixins: [metricsViewMixin],

  props: {
    ranking: {
      type: Object,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    emptyDescription: {
      type: String,
      default: null,
    },
  },

  computed: {
    assignments() {
      return this.ranking?.assignments || [];
    },

    data() {
      return this.ranking?.data || [];
    },

    hasData() {
      return this.data.length > 0;
    },

    rankSummary() {
      if (!this.ranking) return null;
      const { rank, percentile, totalStudents } = this.ranking;
      if (rank == null) return null;
      const className = this.ranking.className || null;
      const classLabel = className
        ? this.$t("metrics.studentRankedInClass", { className })
        : "";
      return this.$t("metrics.studentRankedSummary", {
        rank,
        classLabel,
        percentile,
        total: totalStudents,
      });
    },

    tableItems() {
      return this.data.map((student, index) => ({
        ...student,
        rank: student.rank ?? index + 1,
      }));
    },

    tableFields() {
      const base = [
        { key: "rank", label: "#", style: "width: 40px" },
        {
          key: "student",
          label: this.$t("metrics.student"),
          style: "min-width: 200px",
        },
        {
          key: "avgScore",
          label: this.$t("metrics.avgScore"),
          style: "width: 100px; text-align: center",
        },
      ];
      const assignmentFields = this.assignments.map((assignment) => ({
        key: `assignment_${assignment.id}`,
        label: assignment.name,
        style: "width: 120px; text-align: center",
      }));
      return [...base, ...assignmentFields];
    },
  },

  methods: {
    onRowClick(item) {
      if (!item.studentId || item.isCurrentStudent) return;
      this.goToStudent(item.studentId, this.$route.params.classId);
    },

    initials(student) {
      return studentInitials(student);
    },

    scoreBadgeClass(score) {
      return `metrics-student-class-ranking__score-badge--${getScoreTier(
        score
      )}`;
    },

    rowClass(rowData) {
      return rowData.isCurrentStudent
        ? "metrics-student-class-ranking__row--current"
        : "";
    },
  },
};
</script>

<style lang="scss" scoped>
.metrics-student-class-ranking {
  display: flex;
  flex-direction: column;
  padding: 24px;
  margin: 0 24px;
  background: var(--color-surface-neutral-background, #fff);
  border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  border-radius: 12px;
  gap: 16px;

  &__header {
    display: flex;
    align-items: center;
  }

  &__title {
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-on-white, #1a2330);
  }

  &__summary {
    padding: 10px 14px;
    border-radius: 8px;
    background: var(--color-surface-semantic-informative-light);
    color: var(--color-text-neutral-on-white, #1a2330);
  }

  &__table-wrap {
    ::v-deep .p-datatable-tbody > tr {
      cursor: pointer;
    }

    ::v-deep .metrics-student-class-ranking__row--current td {
      background: var(--color-surface-primary-light-1, #f6f2ff);
    }
  }

  &__student-cell {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--color-surface-primary-light-1, #ede9fe);
    color: var(--color-text-primary-default, #7c3aed);
    font-size: var(--typography-font-size-fs-200, 12px);
    font-weight: var(--typography-font-weight-fw-700, 700);
    flex-shrink: 0;
  }

  &__name {
    font-weight: var(--typography-font-weight-fw-500, 500);
    color: var(--color-text-neutral-on-white, #1a2330);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__current-icon {
    font-size: 20px;
    width: 20px;
    height: 20px;
    color: var(--color-text-primary-default, #7c3aed) !important;
    flex-shrink: 0;
  }

  &__score-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--typography-font-size-fs-200, 12px);
    font-weight: var(--typography-font-weight-fw-600, 600);
    min-width: 52px;

    &--high {
      background: var(--colors-green-100);
      color: var(--color-text-semantic-onSuccess);
    }

    &--medium {
      background: var(--colors-yellow-200);
      color: var(--colors-yellow-800);
    }

    &--low {
      background: var(--color-surface-semantic-error-light);
      color: var(--color-text-semantic-onError);
    }
  }
  ::v-deep
    .p-datatable-thead
    > tr
    > th:nth-child(n + 3)
    .p-column-header-content {
    justify-content: center;
  }

  ::v-deep .p-datatable-tbody > tr > td:nth-child(n + 3) {
    text-align: center;
  }

  ::v-deep
    .metrics-student-class-ranking__row--current
    .metrics-student-class-ranking__avatar {
    border-radius: var(--radius-rd-1000, 100px);
    background: var(--color-surface-primary-default, #8a3ffc);
    color: var(--color-text-on-surface-default);
  }
}
</style>
